import { useMemo, useState } from "react";
import { t } from "i18next";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DefaultButton,
  Dropdown,
  Icon,
  IconButton,
  MessageBar,
  MessageBarType,
  TextField,
} from "@fluentui/react";

import {
  COMBINATOR,
  INPUT_TYPE,
  getFieldDefsForSlot,
  getFieldLabel,
  getInputType,
  getOperatorLabel,
  getOperators,
  isKnownField,
} from "../../helpers/hangingProtocol/fieldRegistry";
import {
  ConditionDslError,
  getCombinator,
  getOperands,
  parseConditionTree,
  printConditionTree,
} from "../../helpers/hangingProtocol/conditionDsl";
import { createEmptyRuleGroup, createRule, newNodeId } from "../../helpers/hangingProtocol/hpDocument";
import {
  duplicateOperand,
  isRuleGroup as isGroup,
  moveOperand,
  reconcileParsedTree,
  removeOperand,
  toInfix,
  wrapOperandInGroup,
} from "../../helpers/hangingProtocol/editorOps";
import { CONDITION_PROBLEM, describeConditionTree, findConditionProblems } from "../../helpers/hangingProtocol/conditionSummary";
import { ReadField } from "./ReadOnly";
import "./conditionBuilder.css";

/**
 * Editor for one condition slot, in both the modes MedDream offers: a visual
 * query builder and the "Condition query text form", kept in sync.
 *
 * Layout follows the group, not the row. Column headers are written once per
 * group and the combinator sits on a rail beside the operands it joins, because
 * the combinator is a property of the group — repeating "Field / Operator /
 * Value" above every row buried that, and made a nested group read as a second
 * unrelated form rather than as one operand of its parent.
 *
 * Three things it does that MedDream's own editor does not:
 *
 *  - the operator list is filtered to what the *field* allows, so you cannot
 *    build a protocol the server will reject (`sop_instance_UID` takes only
 *    contain/not_contain, `scroll_step` only `=`);
 *  - a row can be wrapped in a group after the fact, so reaching
 *    `A && (B || C)` does not mean retyping B and C into a fresh group;
 *  - switching back from text reconciles against the previous tree, preserving
 *    rule `id`s and the variant `path` arrays that a bare re-parse would lose.
 *
 * `value` is always written as a string, including for numeric fields — that is
 * what the format stores, and coercing it breaks export fidelity.
 */

/**
 * Nesting past three levels is unreadable at this width and the DSL round trip
 * gets hard to follow, so the group button stops offering it.
 */
const MAX_GROUP_DEPTH = 2;

/* ------------------------------------------------------------------ drag */

/**
 * Drag state for one operand. The listeners go on the grip alone — spreading
 * them over the whole row would swallow clicks on the dropdowns inside it.
 */
function useOperandDrag(id, disabled) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return {
    ref: setNodeRef,
    style: { transform: CSS.Transform.toString(transform), transition },
    handleProps: { ...attributes, ...listeners },
    isDragging,
  };
}

function Grip({ handleProps, disabled }) {
  if (disabled) return <span className="hpv2-grip-spacer" />;
  return (
    <button
      type="button"
      className="hpv2-grip"
      title={t("hanging-protocol.drag-to-reorder")}
      aria-label={t("hanging-protocol.drag-to-reorder")}
      {...handleProps}
    >
      <Icon iconName="GripperDotsVertical" />
    </button>
  );
}

/**
 * Row affordances for one operand: delete sits in the row itself — it is the
 * action reached most often and hiding it behind an overflow made removing a
 * condition a two-click hunt — while duplicate and wrap stay in the menu.
 * Reordering is not offered here at all; the grip beside it does that.
 */
function OperandActions({ index, canNest, onDuplicate, onWrap, onRemove }) {
  const items = [
    {
      key: "duplicate",
      text: t("hanging-protocol.duplicate"),
      iconProps: { iconName: "Copy" },
      onClick: () => onDuplicate(index),
    },
    {
      key: "wrap",
      text: t("hanging-protocol.wrap-in-group"),
      iconProps: { iconName: "BranchMerge" },
      disabled: !canNest,
      onClick: () => onWrap(index),
    },
  ];

  return (
    <>
      <IconButton
        className="hpv2-row-menu"
        iconProps={{ iconName: "More" }}
        title={t("hanging-protocol.row-actions")}
        ariaLabel={t("hanging-protocol.row-actions")}
        menuProps={{ items }}
        menuIconProps={{ style: { display: "none" } }}
      />
      <IconButton
        className="hpv2-row-delete"
        iconProps={{ iconName: "Delete" }}
        title={t("hanging-protocol.remove")}
        ariaLabel={t("hanging-protocol.remove")}
        onClick={() => onRemove(index)}
      />
    </>
  );
}

/* ------------------------------------------------------------------ rule */

function RuleRow({ rule, dragId, fieldDefs, disabled, onChange, actions }) {
  const inputType = rule.inputType || getInputType(rule.field);
  const operators = getOperators(rule.field);
  const unknownField = rule.field && !isKnownField(rule.field);
  const drag = useOperandDrag(dragId, disabled);

  const fieldOptions = useMemo(() => {
    const options = fieldDefs.map((def) => ({
      key: def.name,
      text: getFieldLabel(def.name),
      title: def.name,
      data: { raw: def.name },
    }));
    // An imported rule may use a field outside this slot's dropdown — the
    // reference export does exactly that. Keep it selectable rather than
    // silently resetting it.
    if (rule.field && !options.some((o) => o.key === rule.field)) {
      options.unshift({
        key: rule.field,
        text: getFieldLabel(rule.field),
        title: rule.field,
        data: { raw: rule.field },
      });
    }
    return options;
  }, [fieldDefs, rule.field]);

  const onFieldChange = (_event, option) => {
    const nextType = getInputType(option.key);
    const nextOperators = getOperators(option.key);
    onChange({
      ...rule,
      field: option.key,
      inputType: nextType,
      // The old operator may not exist for the new field.
      operator: nextOperators.includes(rule.operator) ? rule.operator : nextOperators[0],
      value: nextType === inputType ? rule.value : "",
    });
  };

  /** Friendly label first, the stored name under it — admins search by both. */
  const renderFieldOption = (option) => (
    <span className="hpv2-field-option">
      <span className="hpv2-field-name">{option.text}</span>
      {option.data?.raw !== option.text && <span className="hpv2-field-raw">{option.data?.raw}</span>}
    </span>
  );

  return (
    <div
      ref={drag.ref}
      style={drag.style}
      className={`hpv2-rule ${drag.isDragging ? "is-dragging" : ""}`}
    >
      <Dropdown
        className="hpv2-rule-field"
        ariaLabel={t("hanging-protocol.field")}
        selectedKey={rule.field || null}
        options={fieldOptions}
        disabled={disabled}
        onChange={onFieldChange}
        onRenderOption={renderFieldOption}
        // Fluent sizes the list to the closed control, which cut names like
        // series.PrimaryAnatomicStructureSequence[0008,2228] in half. Let it
        // size to its content instead, capped so it cannot span the dialog.
        dropdownWidth="auto"
        calloutProps={{ className: "hpv2-field-callout", calloutMaxWidth: 460 }}
      />
      <Dropdown
        className="hpv2-rule-operator"
        ariaLabel={t("hanging-protocol.operator")}
        selectedKey={rule.operator || null}
        options={operators.map((op) => ({ key: op, text: getOperatorLabel(op, inputType) }))}
        disabled={disabled}
        onChange={(_e, option) => onChange({ ...rule, operator: option.key })}
      />
      <TextField
        className="hpv2-rule-value"
        ariaLabel={t("hanging-protocol.value")}
        // The column header is hidden on narrow screens; the placeholder keeps
        // the control labelled there.
        placeholder={t("hanging-protocol.value")}
        // Numeric fields still persist their value as a string.
        type={inputType === INPUT_TYPE.NUMERIC ? "number" : "text"}
        value={rule.value ?? ""}
        disabled={disabled}
        onChange={(_e, next) => onChange({ ...rule, value: String(next ?? "") })}
      />
      <Grip handleProps={drag.handleProps} disabled={disabled} />
      {actions}
      {unknownField && <div className="hpv2-rule-note">{t("hanging-protocol.unknown-field")}</div>}
    </div>
  );
}

/* ----------------------------------------------------------------- group */

/** A nested group is one operand of its parent, so it drags and menus like a row. */
function NestedGroupRow({ group, dragId, fieldDefs, disabled, depth, onChange, actions }) {
  const drag = useOperandDrag(dragId, disabled);
  return (
    <div
      ref={drag.ref}
      style={drag.style}
      className={`hpv2-nested-wrapper ${drag.isDragging ? "is-dragging" : ""}`}
    >
      <GroupEditor group={group} fieldDefs={fieldDefs} disabled={disabled} depth={depth} onChange={onChange} />
      <div className="hpv2-nested-actions">
        <Grip handleProps={drag.handleProps} disabled={disabled} />
        {actions}
      </div>
    </div>
  );
}

function GroupEditor({ group, fieldDefs, disabled, depth, onChange }) {
  const operands = getOperands(group);
  const combinator = getCombinator(group);
  const canNest = depth < MAX_GROUP_DEPTH;

  const sensors = useSensors(
    // Without a distance constraint the sensor activates on pointerdown and
    // swallows the click, so the grip would never register as a click either.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Untouched slots carry `id: ""`, so fall back to a positional key rather than
  // handing dnd-kit duplicate ids.
  const dragIds = operands.map((operand, index) => operand.id || `${group.id || "group"}:${index}`);

  const replaceOperands = (next, nextCombinator = combinator) =>
    onChange({ ...group, rules: toInfix(next, nextCombinator) });

  const updateAt = (index, node) =>
    replaceOperands(operands.map((operand, i) => (i === index ? node : operand)));

  const addRule = () => {
    const def = fieldDefs[0];
    replaceOperands([
      ...operands,
      createRule({
        field: def?.name ?? "",
        operator: def?.operations?.[0] ?? "match",
        inputType: def?.inputType ?? INPUT_TYPE.STRING,
        path: def?.path ?? [],
      }),
    ]);
  };

  const addGroup = () => replaceOperands([...operands, createEmptyRuleGroup(newNodeId())]);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = dragIds.indexOf(active.id);
    const to = dragIds.indexOf(over.id);
    if (from === -1 || to === -1) return;
    onChange(moveOperand(group, from, to));
  };

  const actionsFor = (index) =>
    disabled ? null : (
      <OperandActions
        index={index}
        canNest={canNest}
        onDuplicate={(i) => onChange(duplicateOperand(group, i, newNodeId))}
        onWrap={(i) => onChange(wrapOperandInGroup(group, i, newNodeId))}
        onRemove={(i) => onChange(removeOperand(group, i))}
      />
    );

  const combLabel = combinator === COMBINATOR.OR ? "or" : "and";

  return (
    <div
      className={`hpv2-group ${depth > 0 ? "hpv2-group-nested" : ""} ${operands.length > 1 ? "has-comb" : ""}`}
      data-depth={depth}
    >
      {/* Only where there are rule rows to label. A group whose operands are all
          nested groups has no columns of its own, and heading it repeats the
          labels the child groups already carry. */}
      {operands.some((operand) => !isGroup(operand)) && (
        <div className="hpv2-colhead" aria-hidden="true">
          <span>{t("hanging-protocol.field")}</span>
          <span>{t("hanging-protocol.operator")}</span>
          <span>{t("hanging-protocol.value")}</span>
        </div>
      )}

      <div className="hpv2-group-body">
        {/* The rail: a line spanning the operands the combinator joins, with the
            word itself sitting in a break in it. Deliberately the quietest thing
            in the group — a bordered chip competed with the real controls beside
            it, and a menu chevron doubled the width of a three-letter word for a
            choice with only two values. Clicking flips it. */}
        {operands.length > 1 && (
          <div className="hpv2-comb-slot">
            {disabled ? (
              <span className="hpv2-comb is-static">{combLabel}</span>
            ) : (
              <button
                type="button"
                className="hpv2-comb"
                title={t("hanging-protocol.combine-using")}
                onClick={() =>
                  replaceOperands(operands, combinator === COMBINATOR.OR ? COMBINATOR.AND : COMBINATOR.OR)
                }
              >
                {combLabel}
              </button>
            )}
          </div>
        )}

        {operands.length === 0 && <div className="hpv2-group-empty">{t("hanging-protocol.no-conditions")}</div>}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={dragIds} strategy={verticalListSortingStrategy}>
            <div className="hpv2-operands">
              {operands.map((operand, index) =>
                isGroup(operand) ? (
                  <NestedGroupRow
                    key={dragIds[index]}
                    dragId={dragIds[index]}
                    group={operand}
                    fieldDefs={fieldDefs}
                    disabled={disabled}
                    depth={depth + 1}
                    onChange={(next) => updateAt(index, next)}
                    actions={actionsFor(index)}
                  />
                ) : (
                  <RuleRow
                    key={dragIds[index]}
                    dragId={dragIds[index]}
                    rule={operand}
                    fieldDefs={fieldDefs}
                    disabled={disabled}
                    onChange={(next) => updateAt(index, next)}
                    actions={actionsFor(index)}
                  />
                )
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {!disabled && (
        <div className="hpv2-addbar">
          <DefaultButton iconProps={{ iconName: "Add" }} text={t("hanging-protocol.condition")} onClick={addRule} />
          <DefaultButton
            iconProps={{ iconName: "BranchMerge" }}
            text={t("hanging-protocol.group")}
            onClick={addGroup}
            disabled={!canNest}
            title={canNest ? undefined : t("hanging-protocol.max-depth")}
          />
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- builder */

export default function ConditionBuilder({ slot, value, onChange, disabled = false, label }) {
  const [textMode, setTextMode] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState(null);

  const group = value || createEmptyRuleGroup(newNodeId());
  const fieldDefs = useMemo(() => getFieldDefsForSlot(slot), [slot]);

  const summary = describeConditionTree(group, {
    and: t("hanging-protocol.summary-and"),
    or: t("hanging-protocol.summary-or"),
    not: t("hanging-protocol.summary-not"),
    noValue: t("hanging-protocol.summary-no-value"),
  });
  const problems = findConditionProblems(group);
  const missingValues = problems.filter((p) => p.code === CONDITION_PROBLEM.VALUE_REQUIRED).length;

  /* Read mode is the sentence, not the builder. Every row here is three
     dropdowns and a value box, and disabling them leaves a wall of empty-looking
     controls that says nothing a reader could not be told in one line — the same
     line the group list already prints for this slot, so the two agree.

     An empty slot reads "applies always" rather than a dash: no conditions is a
     meaning, and it is the one people get wrong. */
  if (disabled) {
    return <ReadField label={label} value={summary || t("hanging-protocol.applies-always")} wide />;
  }

  const enterTextMode = () => {
    setDraft(printConditionTree(group));
    setError(null);
    setTextMode(true);
  };

  const applyText = () => {
    try {
      const parsed = parseConditionTree(draft);
      onChange(reconcileParsedTree(parsed, group));
      setError(null);
      setTextMode(false);
    } catch (e) {
      setError(e instanceof ConditionDslError ? e.message : String(e));
    }
  };

  return (
    <div className="hpv2-condition-builder">
      <div className="hpv2-condition-header">
        <span className="hpv2-condition-label">{label}</span>
        {textMode ? (
          <div className="hpv2-condition-modes">
            <DefaultButton text={t("button.apply")} onClick={applyText} disabled={disabled} primary />
            <DefaultButton
              text={t("button.cancel")}
              onClick={() => {
                setTextMode(false);
                setError(null);
              }}
            />
          </div>
        ) : (
          <DefaultButton
            iconProps={{ iconName: "Code" }}
            text={t("hanging-protocol.edit-as-text")}
            onClick={enterTextMode}
          />
        )}
      </div>

      {textMode ? (
        <>
          <TextField
            multiline
            rows={4}
            value={draft}
            disabled={disabled}
            onChange={(_e, next) => setDraft(next ?? "")}
            placeholder={'modalities_in_study contain "CT" || modalities_in_study contain "MR"'}
          />
          {error && (
            <MessageBar messageBarType={MessageBarType.error} className="hpv2-condition-error">
              {error}
            </MessageBar>
          )}
        </>
      ) : (
        <>
          <GroupEditor group={group} fieldDefs={fieldDefs} disabled={disabled} depth={0} onChange={onChange} />
          {/* Read the rule back as a sentence: three correct-looking dropdowns
              can still say something the author did not mean. */}
          {summary && (
            <div className={`hpv2-summary ${missingValues ? "has-problem" : ""}`}>
              <Icon iconName={missingValues ? "Warning" : "CheckMark"} className="hpv2-summary-icon" />
              <span>
                {summary}
                {missingValues > 0 && (
                  <span className="hpv2-summary-problem">
                    {t("hanging-protocol.values-missing", { count: missingValues })}
                  </span>
                )}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
